/**
 * Custom Hazard contract — shared wire shape for the user-defined hazards
 * surfaced in the "Select Hazards" step of RAMS generation.
 *
 * Web endpoints have lived at `/api/account-custom-hazards` for some time;
 * the mobile mirror at `/api/mobile/v1/account/custom-hazards` (added with
 * the Settings → Custom Hazards screen) consumes these same schemas via
 * `safeParseOrWarn`.
 *
 * The Prisma model `AccountCustomHazard` carries extra columns
 * (`defaultRiskRating`, `controls`) that the web POST writes through. The
 * mobile UI does NOT expose those today, so the wire schema stays narrow:
 * `{ id, name, description }`. When the mobile UI grows those fields we
 * extend the schema here first.
 */
import { z } from "zod";

/**
 * Field caps — single source for the web routes, the mobile mirror, and the
 * client forms (maxLength). Description/controls were 1,000 until 2026-07-07:
 * users paste multi-paragraph hazard text (often AI-drafted) and the save
 * failed with an unexplained 400 (live support case). The columns are
 * unbounded TEXT in Postgres, so the cap is wire-schema-only — 10,000 keeps a
 * sane bound (the PDF renders whatever is stored) without rejecting real use.
 */
export const CUSTOM_HAZARD_NAME_MAX = 200;
export const CUSTOM_HAZARD_DESCRIPTION_MAX = 10_000;
export const CUSTOM_HAZARD_CONTROLS_MAX = 10_000;

export const CustomHazardSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});
export type CustomHazard = z.infer<typeof CustomHazardSchema>;

export const CustomHazardCreateSchema = z.object({
  name: z.string().min(1).max(CUSTOM_HAZARD_NAME_MAX),
  description: z.string().max(CUSTOM_HAZARD_DESCRIPTION_MAX).optional(),
});
export type CustomHazardCreate = z.infer<typeof CustomHazardCreateSchema>;

export const CustomHazardUpdateSchema = z.object({
  name: z.string().min(1).max(CUSTOM_HAZARD_NAME_MAX).optional(),
  description: z.string().max(CUSTOM_HAZARD_DESCRIPTION_MAX).nullable().optional(),
});
export type CustomHazardUpdate = z.infer<typeof CustomHazardUpdateSchema>;
